import React, { useMemo } from 'react';
import {
  AlertTriangle, BarChart3, Clock, Code2, FileText, GitBranch, Globe, ListChecks,
  MapPin, Network, Send, Table as TableIcon, Trello,
  Component as ReactIcon, File as FileIcon, Image as ImageIcon, Music as AudioIcon, Video as VideoIcon,
} from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { ARTIFACT_KIND_LABELS } from '@easyflow/shared';
import { MarkdownViewer, CodeViewer, DiagramViewer, ReactComponentViewer } from './viewers/DocumentViewers';
import { ChartViewer } from './viewers/ChartViewer';
import { TableViewer } from './viewers/TableViewer';
import { ActionViewer } from './viewers/ActionViewer';
import { FileViewer, ImageViewer, VideoViewer, AudioPlayerViewer } from './viewers/MediaViewers';
import { HtmlViewer } from './viewers/HtmlViewer';
import { FormViewer } from './viewers/FormViewer';
import { MapViewer, TimelineViewer, KanbanViewer, MindmapViewer } from './viewers/StructuredViewers';
import { StubViewer } from './viewers/StubViewer';
import type { ArtifactRegistryEntry, ArtifactViewerProps } from './types';
import type { ArtifactKind } from '@easyflow/shared';

// Registry dei viewer artefatto (Task #3817: split in `viewers/` — questo file
// resta la SOLA mappa kind→Viewer + helper di presentazione, niente monolite).

const REGISTRY: Record<ArtifactKind, ArtifactRegistryEntry['Viewer']> = {
  markdown: MarkdownViewer,
  code: CodeViewer,
  chart: ChartViewer,
  table: TableViewer,
  action: ActionViewer,
  file: FileViewer,
  image: ImageViewer,
  video: VideoViewer,
  audio: AudioPlayerViewer,
  html: HtmlViewer,
  react: ReactComponentViewer,
  form: FormViewer,
  diagram: DiagramViewer,
  map: MapViewer,
  timeline: TimelineViewer,
  kanban: KanbanViewer,
  mindmap: MindmapViewer,
};

export function getKindLabel(kind: ArtifactKind): string {
  return ARTIFACT_KIND_LABELS[kind] ?? kind;
}

export function getKindIcon(kind: ArtifactKind): React.ComponentType<{ className?: string }> {
  switch (kind) {
    case 'code': return Code2;
    case 'chart': return BarChart3;
    case 'table': return TableIcon;
    case 'action': return Send;
    case 'file': return FileIcon;
    case 'image': return ImageIcon;
    case 'video': return VideoIcon;
    case 'audio': return AudioIcon;
    case 'html': return Globe;
    case 'react': return ReactIcon;
    case 'form': return ListChecks;
    case 'diagram': return GitBranch;
    case 'map': return MapPin;
    case 'timeline': return Clock;
    case 'kanban': return Trello;
    case 'mindmap': return Network;
    case 'markdown':
    default: return FileText;
  }
}

interface ViewerErrorBoundaryProps {
  artifactId: string;
  children: React.ReactNode;
}
interface ViewerErrorBoundaryState {
  error: Error | null;
}

// Rete di sicurezza: un errore di rendering di un viewer NON deve mai far
// crashare il workspace — fallback visibile con il messaggio, mai silenzio.
class ViewerErrorBoundary extends React.Component<ViewerErrorBoundaryProps, ViewerErrorBoundaryState> {
  state: ViewerErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ViewerErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prevProps: ViewerErrorBoundaryProps): void {
    // Cambia artefatto → riprova il rendering da zero.
    if (prevProps.artifactId !== this.props.artifactId && this.state.error) {
      this.setState({ error: null });
    }
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="viewer-error">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-red-900">Errore nel rendering dell'artefatto</div>
              <div className="text-xs text-red-700 mt-1 break-words">{this.state.error.message}</div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const ArtifactRenderer: React.FC<ArtifactViewerProps> = ({ artifact, onAction, onFormSubmit }) => {
  const Viewer = useMemo(() => REGISTRY[artifact.kind] ?? StubViewer, [artifact.kind]);
  return (
    <ViewerErrorBoundary artifactId={`${artifact.id}:${artifact.version}`}>
      <Viewer artifact={artifact} onAction={onAction} onFormSubmit={onFormSubmit} />
    </ViewerErrorBoundary>
  );
};

export const KindBadge: React.FC<{ kind: ArtifactKind }> = ({ kind }) => {
  const Icon = getKindIcon(kind);
  return (
    <Badge variant="secondary" className="gap-1 text-[10px]">
      <Icon className="h-3 w-3" />
      {getKindLabel(kind)}
    </Badge>
  );
};
